/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Clapperboard, Loader2, Upload, CheckCircle2, AlertTriangle, Trash2, Play } from "lucide-react";

const fmtSize = (n) => n > 1e6 ? `${(n / 1e6).toFixed(1)} Mo` : `${Math.round(n / 1e3)} ko`;

function UploadRow({ ex, onChanged }) {
  const inputRef = useRef(null);
  const [prog, setProg] = useState(null);
  const [statut, setStatut] = useState(ex.video?.statut || null);
  const [preview, setPreview] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);
  useEffect(() => { setStatut(ex.video?.statut || null); }, [ex.video?.statut]);

  const poll = (uploadId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/videos/upload/${uploadId}`);
        setStatut(data.statut);
        if (["pret", "echec"].includes(data.statut)) { clearInterval(pollRef.current); setProg(null); onChanged(); data.statut === "pret" ? toast.success(`Vidéo « ${ex.titre} » prête avec filigrane Bombacare`) : toast.error(`Traitement échoué : ${data.erreur || ""}`); }
      } catch { /* retry */ }
    }, 3000);
  };

  const send = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Choisissez un fichier vidéo (mp4, mov, webm)");
    setProg(0);
    try {
      const { data: init } = await api.post("/videos/upload/init", { exercice_id: ex.id, filename: file.name, taille: file.size, mime: file.type });
      for (let i = 0; i < init.chunks_attendus; i++) {
        const blob = file.slice(i * init.chunk, (i + 1) * init.chunk);
        let ok = false;
        for (let tent = 0; tent < 3 && !ok; tent++) {
          try { await api.put(`/videos/upload/${init.upload_id}/chunk/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }); ok = true; }
          catch (e) { if (tent === 2) throw e; await new Promise((r) => setTimeout(r, 1500)); }
        }
        setProg(Math.round(((i + 1) / init.chunks_attendus) * 100));
      }
      await api.post(`/videos/upload/${init.upload_id}/finish`);
      setStatut("traitement"); toast.message("Vidéo reçue — incrustation du filigrane en cours…");
      poll(init.upload_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Envoi interrompu"); setProg(null); }
  };

  const retirer = async () => {
    if (!window.confirm(`Retirer la vidéo originale de « ${ex.titre} » ? Les patients reverront la vidéo par défaut.`)) return;
    try { await api.delete(`/videos/${ex.id}`); toast.success("Vidéo retirée"); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const v = ex.video;
  const pret = statut === "pret" && v?.statut === "pret";
  return (
    <div data-testid={`video-row-${ex.id}`} className="py-3 border-b border-[#F5F3EE] last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${pret ? "bg-[#2BA3A0] text-white" : statut === "echec" ? "bg-[#D9534F]/15 text-[#D9534F]" : "bg-[#F5F3EE] text-[#0A1426]/60"}`}>{statut === "traitement" || prog !== null ? <Loader2 size={18} className="animate-spin" /> : pret ? <CheckCircle2 size={18} /> : statut === "echec" ? <AlertTriangle size={18} /> : <Clapperboard size={18} />}</span>
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold text-[14px] text-[#0A1426]">{ex.titre} <span className="text-[11px] font-normal text-[#0A1426]/50">· {ex.id}</span></div>
          <div data-testid={`video-statut-${ex.id}`} className="text-[12px] text-[#0A1426]/60">
            {prog !== null ? `Envoi ${prog} %` : statut === "traitement" ? "Incrustation du filigrane « Bombacare © » en cours…" : pret ? `Vidéo originale en ligne · ${fmtSize(v.taille_finale || 0)} · filigrane incrusté · ${new Date(v.traite_le).toLocaleDateString("fr-FR")}` : statut === "echec" ? `Échec : ${v?.erreur || "traitement"}` : ex.video_defaut ? "Vidéo de démonstration par défaut (à remplacer par l'originale)" : "Aucune vidéo — « vidéo Bombacare à venir » côté patient"}
          </div>
          <div className="text-[11px] text-[#0A1426]/50">{ex.pathologies.join(" · ")}</div>
        </div>
        {pret && <button data-testid={`video-preview-${ex.id}`} onClick={() => setPreview(!preview)} className="h-10 px-3 rounded-full bg-[#F5F3EE] text-[#0A1426] text-[12px] font-bold inline-flex items-center gap-1"><Play size={14} /> {preview ? "Fermer" : "Aperçu"}</button>}
        {pret && <button data-testid={`video-delete-${ex.id}`} onClick={retirer} className="w-10 h-10 rounded-full border border-[#E4E0D5] text-[#D9534F] grid place-items-center"><Trash2 size={16} /></button>}
        <input ref={inputRef} data-testid={`video-input-${ex.id}`} type="file" accept="video/*" className="hidden" onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} />
        <Button data-testid={`video-upload-${ex.id}`} disabled={prog !== null || statut === "traitement"} onClick={() => inputRef.current?.click()} className="h-10 rounded-full bg-[#0A1426] text-white text-[12px] font-bold"><Upload size={14} className="mr-1" /> {pret ? "Remplacer" : "Déposer la vidéo"}</Button>
      </div>
      {prog !== null && <div className="h-2 bg-[#F5F3EE] rounded-full overflow-hidden mt-2"><div data-testid={`video-progress-${ex.id}`} className="h-full bg-[#2BA3A0] transition-[width]" style={{ width: `${prog}%` }} /></div>}
      {preview && pret && <video data-testid={`video-player-${ex.id}`} controls playsInline className="mt-3 w-full max-w-md rounded-2xl bg-black" src={`${API}/videos/${ex.id}/stream?v=${encodeURIComponent(v.traite_le)}`} />}
    </div>
  );
}

export default function VideosPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/videos/exercices").then(({ data }) => setD(data)).catch((e) => setErr(e.response?.status));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (err === 403) return <div data-testid="videos-no-access" className="bc-in bg-white rounded-2xl border border-[#E4E0D5] p-6 text-[13px] text-[#0A1426]">Le dépôt des vidéos originales est réservé au centre racine Bombacare (fondateur).</div>;
  if (!d) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  const prets = d.exercices.filter((e) => e.video?.statut === "pret").length;
  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-videos-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#0A1426] text-white grid place-items-center"><Clapperboard size={20} /></div>
        <div>
          <h1 className="font-[Fraunces] text-2xl md:text-3xl font-bold text-[#0A1426]">Vidéos originales Bombacare</h1>
          <div className="text-[12px] text-[#0A1426]/60">Une vidéo par exercice des 5 pathologies · filigrane « Bombacare © » incrusté automatiquement · {prets}/{d.exercices.length} en ligne</div>
        </div>
      </div>
      <div className="text-[12px] text-[#0A1426]/70 bg-[#F5F3EE] rounded-xl px-3 py-2">Formats : mp4, mov, webm, mkv · jusqu'à {fmtSize(d.max_size)} · envoi par morceaux de {fmtSize(d.chunk)} (reprise automatique) · ré-encodage 720p + filigrane côté serveur, puis diffusion aux patients et mise en cache hors-ligne.</div>
      <div className="bg-white rounded-2xl border border-[#E4E0D5] p-4" data-testid="videos-list">
        {d.exercices.map((ex) => <UploadRow key={ex.id} ex={ex} onChanged={load} />)}
      </div>
    </div>
  );
}
